import { jsPDF } from "jspdf";
import "jspdf-autotable";
import logo from "assets/images/logo_nova_4d_azul.png";

// Função auxiliar para formatar texto markdown
const formatMarkdownText = text => {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/_(.*?)_/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\n\s*\n/g, "\n")
    .replace(/^\s*[-*+]\s*/gm, "• ")
    .replace(/^\s*\d+\.\s*/gm, "")
    .trim();
};

// Função auxiliar para formatar data
const formatDate = dateString => {
  const options = { day: "2-digit", month: "2-digit", year: "numeric" };
  return new Date(dateString).toLocaleDateString("pt-BR", options);
};

// Função auxiliar para formatar data para nome de arquivo
const formatDateForFilename = dateString => {
  const date = new Date(dateString);
  return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

// Função auxiliar para carregar imagem
const loadImage = url => {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
  });
};

export const generatePDFReport = async (
  filePairs,
  project,
  date,
  xResumoData = null,
  annotatedImagesUrls = [],
  customLogo = null,
  orientation = "portrait"
) => {
  const doc = new jsPDF({
    orientation: orientation,
    unit: "mm",
    format: "a4"
  });

  // Logo
  const logoSrc = customLogo || logo;
  if (logoSrc) {
    const imgData = await loadImage(logoSrc);
    if (imgData) doc.addImage(imgData, "JPEG", 15, 5, 40, 20);
  }

  // Título
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("RELATÓRIO DE INSPEÇÃO TÉCNICA", 105, 20, { align: "center" });

  // Informações do projeto
  doc.setFontSize(10);
  doc.text(`Projeto: ${project.name}`, 15, 40);
  doc.text(`Tipo: ${project.type}`, 15, 45);
  doc.text(`Data: ${formatDate(date)}`, 15, 50);

  // Linha divisória
  doc.setDrawColor(200, 200, 200);
  doc.line(15, 55, 195, 55);

  // Adicionar cada item em uma página exclusiva
  for (let i = 0; i < filePairs.length; i++) {
    const pair = filePairs[i];

    // Adicionar nova página para cada item (exceto o primeiro)
    if (i > 0) {
      doc.addPage();
    }

    // Posição inicial baseada na página (primeira página tem cabeçalho)
    let yPosition = i === 0 ? 70 : 20;

    // Título do item
    doc.setFontSize(14);
    doc.setTextColor(3, 81, 132);
    doc.setFont("helvetica", "bold");
    doc.text(`Item ${i + 1}: ${pair.title}`, 15, yPosition);
    yPosition += 10;

    // Imagem (se existir)
    if (pair.imageUrl) {
      try {
        const imgData = await loadImage(pair.imageUrl);
        const imgWidth = 180;
        const imgHeight = (imgData.height * imgWidth) / imgData.width;

        // Verifica se há espaço na página atual
        if (yPosition + imgHeight > 250) {
          doc.addPage();
          yPosition = 20; // Nova página sempre começa em Y=20
        }

        doc.addImage(imgData, "JPEG", 15, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 10;
      } catch (error) {
        console.error("Erro ao carregar imagem:", error);
      }
    }

    // Descrição
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text("Descrição:", 15, yPosition);
    yPosition += 7;

    doc.setFont("helvetica", "normal");
    const formattedDescription = formatMarkdownText(pair.description);
    const splitDesc = doc.splitTextToSize(formattedDescription, 180);
    doc.text(splitDesc, 15, yPosition);
    yPosition += splitDesc.length * 5 + 10;

    // Ação recomendada
    doc.setFont("helvetica", "bold");
    doc.text("Ação Recomendada:", 15, yPosition);
    yPosition += 7;
    doc.setFont("helvetica", "normal");
    const formattedAction = formatMarkdownText(pair.action);
    const splitAction = doc.splitTextToSize(formattedAction, 180);
    doc.text(splitAction, 15, yPosition);
    yPosition += splitAction.length * 5 + 10;

    // Severidade
    doc.setFont("helvetica", "bold");
    doc.text("Severidade:", 15, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(pair.severity, 40, yPosition);
  }

  // Adicionar resumo.json (e xresumo.json se houver)
  await addXResumoPage(doc, project, date, xResumoData);

  // Imagens anotadas, cada uma em uma página
  await addAnnotatedImagesPages(doc, annotatedImagesUrls);

  // Rodapé
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(
      `Página ${i} de ${pageCount} | Gerado em: ${new Date().toLocaleDateString()} | Vertex4D`,
      105,
      285, // Reduzido de 287 para 285
      { align: "center" }
    );
  }

  doc.save(`Relatorio_${project.name}_${formatDateForFilename(date)}.pdf`);
};

// Função para adicionar página com dados do xresumo.json
const addXResumoPage = async (doc, project, date, xResumoData = null) => {
  try {
    // Se xResumoData não foi passado, tentar buscar
    if (!xResumoData) {
      // Construir URL do arquivo xresumo.json
      const folderPath = `projetos/${project.name}/${date}/relatorios`;
      const xResumoUrl = `${process.env.REACT_APP_API_URL ||
        "http://localhost:8000"}/files/${folderPath}/xresumo.json`;

      // Buscar dados do xresumo.json
      const response = await fetch(xResumoUrl);
      if (!response.ok) {
        return;
      }

      xResumoData = await response.json();
    }

    // Adicionar nova página
    doc.addPage();

    // Inicializar posição Y
    let yPosition = 30;

    // Função auxiliar para verificar espaço e adicionar nova página se necessário
    const checkAndAddPage = requiredSpace => {
      if (yPosition + requiredSpace > 240) {
        doc.addPage();
        yPosition = 30;
        return true;
      }
      return false;
    };

    // Função auxiliar para adicionar título de seção
    const addSectionTitle = title => {
      checkAndAddPage(20);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(3, 81, 132);
      doc.text(title, 15, yPosition);
      yPosition += 6;
    };

    // Função auxiliar para adicionar título principal
    const addMainTitle = title => {
      checkAndAddPage(25);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(3, 81, 132);
      doc.text(title, 105, yPosition, { align: "center" });
      yPosition += 12;
    };

    // Função auxiliar para adicionar texto com quebra automática
    const addTextWithBreak = (text, fontSize = 10, isBold = false) => {
      if (!text || text.trim() === "") return;

      const lines = text.split("\n");
      let currentY = yPosition;

      for (const line of lines) {
        if (!line.trim()) {
          currentY += 2;
          continue;
        }

        const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);

        if (headerMatch) {
          const headerLevel = headerMatch[1].length;
          const headerText = headerMatch[2];

          let headerFontSize, headerSpacing;
          switch (headerLevel) {
            case 1:
              headerFontSize = 16;
              headerSpacing = 10;
              break;
            case 2:
              headerFontSize = 14;
              headerSpacing = 8;
              break;
            case 3:
              headerFontSize = 12;
              headerSpacing = 6;
              break;
            case 4:
              headerFontSize = 11;
              headerSpacing = 5;
              break;
            default:
              headerFontSize = 10;
              headerSpacing = 4;
              break;
          }

          checkAndAddPage(headerSpacing + 8);
          doc.setFontSize(headerFontSize);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(3, 81, 132);
          doc.text(headerText, 15, currentY);
          currentY += headerSpacing + 3;

          doc.setTextColor(0, 0, 0);
          doc.setFontSize(fontSize);
          doc.setFont("helvetica", isBold ? "bold" : "normal");
        } else {
          const formattedLine = formatMarkdownText(line);
          if (formattedLine.trim()) {
            doc.setFontSize(fontSize);
            doc.setFont("helvetica", isBold ? "bold" : "normal");
            doc.setTextColor(0, 0, 0);

            const splitText = doc.splitTextToSize(formattedLine, 170);
            const textHeight = splitText.length * 6;

            checkAndAddPage(textHeight + 8);
            doc.text(splitText, 15, currentY);
            currentY += textHeight + 6; // Reduzido o espaçamento entre linhas
          }
        }
      }

      yPosition = currentY;
    };

    // Função auxiliar para adicionar lista formatada
    const addFormattedList = (items, title = null) => {
      if (title) {
        addSectionTitle(title);
      }

      if (Array.isArray(items)) {
        items.forEach((item, index) => {
          if (item && item.trim()) {
            const formattedItem = `• ${item.trim()}`;
            addTextWithBreak(formattedItem);
          }
        });
      } else if (typeof items === "string") {
        const lines = items.split("\n").filter(line => line.trim());
        lines.forEach((line, index) => {
          const formattedItem = `• ${line.trim()}`;
          addTextWithBreak(formattedItem);
        });
      }
    };

    // Função auxiliar para adicionar parágrafo formatado
    const addFormattedParagraph = (text, title = null) => {
      if (title) {
        addSectionTitle(title);
      }

      if (text && text.trim()) {
        // Dividir por quebras de linha duplas para identificar parágrafos
        const paragraphs = text.split("\n\n").filter(p => p.trim());

        paragraphs.forEach((paragraph, index) => {
          if (paragraph.trim()) {
            // Remover quebras de linha simples dentro do parágrafo e substituir por espaços
            const cleanParagraph = paragraph.replace(/\n/g, " ").trim();

            // Adicionar recuo apenas se não for o primeiro parágrafo ou se não tiver título
            const formattedParagraph =
              index === 0 && title ? cleanParagraph : `  ${cleanParagraph}`;
            addTextWithBreak(formattedParagraph);

            // Adicionar espaço extra entre parágrafos (exceto após o último)
            if (index < paragraphs.length - 1) {
              yPosition += 1;
            }
          }
        });
      }
    };

    // Função auxiliar para adicionar texto contínuo (sem bullets)
    const addContinuousText = (text, title = null) => {
      if (title) {
        addSectionTitle(title);
      }

      if (text && text.trim()) {
        // Se o texto contém quebras de linha duplas, tratar como parágrafos
        if (text.includes("\n\n")) {
          addFormattedParagraph(text);
        } else {
          // Caso contrário, tratar como texto contínuo
          const cleanText = text.replace(/\n/g, " ").trim();
          addTextWithBreak(cleanText);
        }
      }
    };

    // Função para processar dados JSON mal formatados
    const processJsonData = data => {
      if (!data) return data;

      if (typeof data === "string") {
        try {
          let cleanString = data
            .replace(/\\n/g, "\n")
            .replace(/\\"/g, '"')
            .replace(/\\\\/g, "\\")
            .trim();

          if (cleanString.startsWith("```json\n")) {
            cleanString = cleanString.substring(7);
          }
          if (cleanString.endsWith("```")) {
            cleanString = cleanString.substring(0, cleanString.length - 3);
          }

          return JSON.parse(cleanString);
        } catch (e) {
          return data;
        }
      }

      if (typeof data === "object") {
        return data;
      }

      return data;
    };

    // === PRIMEIRA PÁGINA DO RESUMO ===
    // Título da página
    addMainTitle("RESUMO EXECUTIVO");

    // Linha divisória
    doc.setDrawColor(200, 200, 200);
    doc.line(15, yPosition, 195, yPosition);
    yPosition += 5;

    // Informações do projeto
    addSectionTitle("Informações do Projeto:");

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    addTextWithBreak(`Projeto: ${project.name}`);
    addTextWithBreak(`Tipo: ${project.type}`);
    addTextWithBreak(`Data: ${formatDate(date)}`);

    yPosition += 5;

    // Se resumo.json tem parecer completo (gerado pela IA), adicionar primeiro
    if (xResumoData && xResumoData.parecer && typeof xResumoData.parecer === "string") {
      addSectionTitle("Parecer da IA:");
      addTextWithBreak(xResumoData.parecer);
      yPosition += 5;
    }

    // Extrair dados do JSON de forma mais inteligente
    let extractedData = xResumoData;

    // Se o JSON tem uma estrutura aninhada, extrair o conteúdo
    if (
      xResumoData &&
      xResumoData.resumo &&
      typeof xResumoData.resumo === "string"
    ) {
      try {
        extractedData = processJsonData(xResumoData.resumo);
      } catch (e) {
      }
    } else if (xResumoData && typeof xResumoData === "object") {
      extractedData = processJsonData(xResumoData);
    }

    // === PRIMEIRA PÁGINA: Informações do Projeto + Sumário dos Pontos ===

    // Sumário dos Pontos (primeira página)
    if (extractedData.sumario_pontos) {;
      const sumarioText = Array.isArray(extractedData.sumario_pontos)
        ? extractedData.sumario_pontos.join("\n\n")
        : extractedData.sumario_pontos;
      addContinuousText(sumarioText, "Sumário dos Pontos:");
      yPosition += 1;
    }

    // === SEGUNDA PÁGINA: Oportunidades de Melhoria + Referências ===
    if (extractedData.oportunidades_melhoria || extractedData.referencias) {
      doc.addPage();
      yPosition = 30;

      // Título da segunda página
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(3, 81, 132);
      doc.text("RESUMO EXECUTIVO - CONTINUAÇÃO", 105, yPosition, {
        align: "center"
      });
      yPosition += 10;

      // Oportunidades de Melhoria
      if (extractedData.oportunidades_melhoria) {;
        const oportunidadesText = Array.isArray(
          extractedData.oportunidades_melhoria
        )
          ? extractedData.oportunidades_melhoria.join("\n\n")
          : extractedData.oportunidades_melhoria;
        addContinuousText(oportunidadesText, "Oportunidades de Melhoria:");
        yPosition += 1;
      }

      // Referências
      if (extractedData.referencias) {;
        const referenciasText = Array.isArray(extractedData.referencias)
          ? extractedData.referencias.join("\n\n")
          : extractedData.referencias;
        addContinuousText(referenciasText, "Referências:");
        yPosition += 1;
      }
    }

    // === TERCEIRA PÁGINA: Conclusão e Recomendações ===
    if (extractedData.conclusao_recomendacoes) {;

      doc.addPage();
      yPosition = 30;

      // Título da terceira página
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(3, 81, 132);
      doc.text("RESUMO EXECUTIVO - CONCLUSÃO", 105, yPosition, {
        align: "center"
      });
      yPosition += 10;

      const conclusaoText = Array.isArray(extractedData.conclusao_recomendacoes)
        ? extractedData.conclusao_recomendacoes.join("\n\n")
        : extractedData.conclusao_recomendacoes;
      addContinuousText(conclusaoText, "Conclusão e Recomendações:");
      yPosition += 1;
    }

    // Verificar se há outros campos que podem conter dados importantes (primeira página)
    const otherFields = [
      "resumo",
      "conclusao",
      "recomendacoes",
      "observacoes",
      "notas"
    ];
    let hasOtherData = false;

    otherFields.forEach(field => {
      if (extractedData[field] && !hasOtherData) {
        const fieldValue = extractedData[field];

        // Sempre usar texto contínuo para o resumo, sem bullets
        if (Array.isArray(fieldValue)) {
          const fieldText = fieldValue.join("\n\n");
          addContinuousText(
            fieldText,
            `${field.charAt(0).toUpperCase() + field.slice(1)}:`
          );
        } else if (typeof fieldValue === "string") {
          addContinuousText(
            fieldValue,
            `${field.charAt(0).toUpperCase() + field.slice(1)}:`
          );
        } else {
          addContinuousText(
            String(fieldValue),
            `${field.charAt(0).toUpperCase() + field.slice(1)}:`
          );
        }

        hasOtherData = true;
      }
    });

    // Se nenhuma seção foi encontrada, exibir o JSON bruto para debug (primeira página)
    if (
      !extractedData.sumario_pontos &&
      !extractedData.oportunidades_melhoria &&
      !extractedData.referencias &&
      !extractedData.conclusao_recomendacoes &&
      !hasOtherData
    ) {
      const availableFields = Object.keys(extractedData || {}).filter(
        key =>
          extractedData[key] &&
          typeof extractedData[key] !== "object" &&
          key !== "resumo"
      );

      if (availableFields.length > 0) {
        availableFields.forEach(field => {
          const fieldValue = extractedData[field];

          // Sempre usar texto contínuo para o resumo, sem bullets
          if (Array.isArray(fieldValue)) {
            const fieldText = fieldValue.join("\n\n");
            addContinuousText(
              fieldText,
              `${field.charAt(0).toUpperCase() + field.slice(1)}:`
            );
          } else if (typeof fieldValue === "string") {
            addContinuousText(
              fieldValue,
              `${field.charAt(0).toUpperCase() + field.slice(1)}:`
            );
          } else {
            addContinuousText(
              String(fieldValue),
              `${field.charAt(0).toUpperCase() + field.slice(1)}:`
            );
          }
        });
      } else {
        addSectionTitle("Dados Brutos do Resumo:");
        const jsonString = JSON.stringify(extractedData, null, 2);
        addTextWithBreak(jsonString, 9);
      }
    }
  } catch (error) {
    doc.addPage();
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(100, 100, 100);
    doc.text("Resumo Executivo", 105, 50, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Dados do resumo executivo não estão disponíveis.", 105, 70, {
      align: "center"
    });
  }
};

// Adiciona imagens anotadas, cada uma em uma página
const addAnnotatedImagesPages = async (doc, annotatedImagesUrls = []) => {
  if (!annotatedImagesUrls || annotatedImagesUrls.length === 0) return;
  const imgFormat = url => (/\.png$/i.test(url) ? "PNG" : "JPEG");
  for (let i = 0; i < annotatedImagesUrls.length; i++) {
    const url = annotatedImagesUrls[i];
    try {
      doc.addPage();
      const imgData = await loadImage(url);
      if (!imgData) continue;
      const imgWidth = 180;
      const imgHeight = Math.min((imgData.height * imgWidth) / imgData.width, 250);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(3, 81, 132);
      doc.text(`Imagem anotada ${i + 1}`, 15, 20);
      doc.addImage(imgData, imgFormat(url), 15, 25, imgWidth, imgHeight);
    } catch (err) {
      console.error("Erro ao carregar imagem anotada:", url, err);
    }
  }
};
